import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiErrorMsg, formatINR, resolveImage, formatSize } from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, X, Clock, CheckCircle, XCircle, Layers, BadgeCheck, Wallet, Phone, MessageSquare, AlertTriangle, ShieldCheck, Building2, FileEdit, Send, Lock, Crown, ArrowUpRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import BuilderProjectsTab from "./BuilderProjectsTab";
import DealerExplore from "./DealerExplore";
import DealerPlanCard from "./DealerPlanCard";
// v2.71.69 — UnifiedSubmissionForm is 1744 lines; only opens when user
// clicks "Add Listing" or "Edit". Lazy-loaded to keep dealer-dashboard
// initial bundle lean.
const UnifiedSubmissionForm = lazy(() => import("../components/UnifiedSubmissionForm"));
import { DashboardSkeleton } from "../components/Skeletons";
import PropertyCard from "../shared/components/PropertyCard";
import ViewToggle, { useViewToggle } from "../shared/components/ViewToggle";
import { scrollToResults } from "../lib/scrollToResults";

const ID_TYPES = ["Aadhaar", "PAN", "DL", "Passport"];

const STATUS_VISUAL = {
  draft: { icon: <FileEdit size={12} />, cls: "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]", label: "Draft" },
  pending: { icon: <Clock size={12} />, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", label: "Pending review" },
  approved: { icon: <CheckCircle size={12} />, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]", label: "Approved · live" },
  rejected: { icon: <XCircle size={12} />, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]", label: "Rejected" },
};

export default function DealerDashboard({ defaultTab, embedded = false } = {}) {
  const { user, refreshUser, isBuilder, loading: authLoading } = useAuth();
  const { settings } = useConfig();
  const freeMode = !!settings?.free_mode;
  const consoleLabel = isBuilder ? "Builder / Owner Console" : "Dealer Console";
  const consoleSubtitle = isBuilder
    ? "Submit new-construction projects for admin review. Client contact stays with House Entry."
    : "Submit properties for approval. Client contact stays centralized with admin.";
  const [submissions, setSubmissions] = useState([]);
  const [listingStatus, setListingStatus] = useState(null);
  const [myPlan, setMyPlan] = useState({ active: false });
  const [kyc, setKyc] = useState(null);
  const [verified, setVerified] = useState(false);
  const [commission, setCommission] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  // v2.54 — Phase C Part 2: Edit own listing dialog
  const [editingListing, setEditingListing] = useState(null);
  const [showListingForm, setShowListingForm] = useState(false);
  // v2.42 — sync tab with URL ?tab=. Fixes "Leads button not opening" from
  // mobile bottom-nav and lets deep-links (like /dealer?tab=enquiries) work.
  const loc = useLocation();
  const nav = useNavigate();
  const _ALLOWED_TABS = ["submit", "submissions", "enquiries", "commission", "kyc", "projects", "explore", "plan"];
  // v2.71.33 — Route segment → internal tab value. The new route-based
  // dashboard uses friendly URLs (`/dealer/listings`, `/dealer/leads`,
  // `/dealer/earnings`) while DealerDashboard internally still uses the
  // original tab values. Alias resolves the route name to the legacy tab.
  const _ROUTE_ALIAS = { listings: "submissions", leads: "enquiries", earnings: "commission" };
  // Reverse map: internal tab value → route segment, used for embedded nav.
  const _TAB_TO_ROUTE = { submissions: "listings", enquiries: "leads", commission: "earnings" };
  const _resolveTab = (raw) => {
    if (!raw) return null;
    const t = _ROUTE_ALIAS[raw] || raw;
    return _ALLOWED_TABS.includes(t) ? t : null;
  };
  const initialTab = (() => {
    const fromDefault = _resolveTab(defaultTab);
    if (fromDefault) return fromDefault;
    const fromQuery = _resolveTab(new URLSearchParams(loc.search).get("tab"));
    return fromQuery || "submit";
  })();
  const [tab, setTab] = useState(initialTab);
  // v2.71.7 — sublist filter for "My Listings" (driven by ?filter= URL param
  // from clickable overview cards; default = all)
  const [listFilter, setListFilter] = useState(() => new URLSearchParams(loc.search).get("filter") || "all");
  // v2.71.53 — Card/Table view toggle for dealer listings (default cards).
  const [submissionsView, setSubmissionsView] = useViewToggle("dealer-submissions", "cards");

  // Keep state in sync if URL changes externally (back button, mobile nav tap)
  useEffect(() => {
    // v2.71.32 — When embedded (route-based wrapper), defaultTab is authoritative;
    // URL ?tab= isn't read.
    if (embedded) {
      const t = _resolveTab(defaultTab);
      if (t && t !== tab) setTab(t);
      return;
    }
    const t = _resolveTab(new URLSearchParams(loc.search).get("tab"));
    if (t && t !== tab) setTab(t);
    const f = new URLSearchParams(loc.search).get("filter") || "all";
    if (f !== listFilter) setListFilter(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search, defaultTab, embedded]);

  // When the user clicks a tab inside the dashboard, push it to the URL too
  const onTabChange = (next) => {
    setTab(next);
    if (embedded) {
      nav(`/dealer/${_TAB_TO_ROUTE[next] || next}`, { replace: true });
    } else {
      const params = new URLSearchParams(loc.search);
      params.set("tab", next);
      nav({ search: `?${params.toString()}` }, { replace: true });
    }
  };

  const load = async (silent = false) => {
    try {
      const [subs, status, kycRes, comm, enq, plan] = await Promise.all([
        api.get("/dealer/my-submissions"),
        api.get("/dealer/listing-status"),
        api.get("/dealer/kyc"),
        // v2.35 — PART 2: skip commission API for builders (returns 0s anyway)
        isBuilder ? Promise.resolve({ data: null }) : api.get("/dealer/commission"),
        api.get("/dealer/enquiries"),
        api.get("/dealer/my-plan").catch(() => ({ data: { active: false } })),
      ]);
      setSubmissions(subs.data);
      setListingStatus(status.data);
      setKyc(kycRes.data.kyc);
      setVerified(kycRes.data.verified_partner);
      setCommission(comm.data);
      setEnquiries(enq.data);
      setMyPlan(plan.data || { active: false });
    } catch (err) { if (silent !== true) toast.error(apiErrorMsg(err)); }
  };

  // v2.49 — wait for auth to settle before firing protected fetches.
  // Without this guard, a hard-refresh on /dealer races: load() runs while
  // AuthContext is still bootstrapping → /api/dealer/* returns 401 → axios
  // interceptor logs the user out → "dashboard crashed" UX.
  useEffect(() => {
    if (!authLoading && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  // v2.72 — Live sync: submissions/status reflect instantly after any
  // listing mutation (own create/edit/delete, admin approval via WS
  // piggyback) + a silent 45 s background refresh while the tab is visible.
  useDataSync(["properties", "approvals"], () => { if (user) load(true); });
  useAutoRefresh(() => { if (user) load(true); }, 45000);

  // Show a steady loading state instead of half-rendering an empty dashboard
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="dealer-loading">
        <DashboardSkeleton stats={4} rows={5} />
      </div>
    );
  }
  // If somehow we land here without a user (e.g. token expired mid-page),
  // bounce to login so we never render a broken protected page.
  if (!user) {
    if (typeof window !== "undefined") {
      window.location.replace(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
    return null;
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#FAFAFA]"}>
      <div className={embedded ? "" : "max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-8"}>
        {!embedded && (
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF] mb-1" data-testid="console-label">{consoleLabel}</div>
            <h1 className="font-heading font-bold text-3xl tracking-tight flex items-center gap-2">
              Welcome, {user?.name}
              {verified && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0] rounded-sm" data-testid="verified-partner-badge">
                  <BadgeCheck size={11} /> Verified Partner
                </span>
              )}
            </h1>
            <p className="text-[13px] text-[#52525B] mt-1">{consoleSubtitle}</p>
          </div>
        </div>
        )}
        {/* v2.71.36 — Embedded mode: surface the dealer's primary CTA on every
            route except /dealer/submit (which IS the Add Property form). */}
        {embedded && tab !== "submit" && (
          <div className="flex items-center gap-2 flex-wrap justify-end mb-4" data-testid="dealer-embedded-actions">
            {verified && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0] rounded-sm" data-testid="verified-partner-badge">
                <BadgeCheck size={11} /> Verified Partner
              </span>
            )}
            <Button onClick={() => onTabChange("submit")} className="btn-brand rounded-sm" data-testid="dealer-add-property-btn">
              <Plus size={15} className="mr-1.5" /> Add Property
            </Button>
          </div>
        )}

        {/* Quota banner — hidden in free mode (SSOT) */}
        {!freeMode && listingStatus && (
          <div className={`mb-5 p-3 border rounded-sm flex items-center justify-between flex-wrap gap-2 ${
            listingStatus.must_upgrade ? "bg-[#FEE2E2] border-[#FECACA] text-[#991B1B]" :
            listingStatus.free_remaining <= 2 ? "bg-[#FEF3C7] border-[#FDE68A] text-[#92400E]" :
            "bg-[#EEF4FF] border-[#C7D9FF] text-[#0044CC]"
          }`} data-testid="dealer-quota-banner">
            <div className="text-[13px] flex items-center gap-2">
              {listingStatus.must_upgrade ? <AlertTriangle size={14} /> : <Layers size={14} />}
              {listingStatus.must_upgrade
                ? <span><b>Free quota reached</b> — you've used all {listingStatus.free_quota} free listings. Contact admin to enable a subscription.</span>
                : <span><b>{listingStatus.total_submitted}</b> / {listingStatus.free_quota} free listings used · <b>{listingStatus.free_remaining}</b> remaining</span>}
            </div>
          </div>
        )}
        {freeMode && (
          <div className="mb-5 p-3 border bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D] rounded-sm text-[13px] flex items-center gap-2" data-testid="dealer-free-mode-banner">
            <CheckCircle size={14} /> <span><b>Free Listing Mode is ON</b> — submit unlimited listings &amp; projects, no plan/quota needed.</span>
          </div>
        )}

        {/* v2.71.7 — Quick stats: clickable overview cards (matches admin UX) */}
        {(() => {
          const pendingCount = submissions.filter((s) => s.approval_status === "pending").length;
          const soldCount = submissions.filter((s) => s.status === "Sold").length;
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="dealer-overview-cards">
              <OverviewCard
                icon={<Layers size={14} />}
                label={isBuilder ? "My Inventory" : "My Listings"}
                value={submissions.length}
                onClick={() => { setListFilter("all"); nav("/dealer/listings"); }}
                accent
                testid="dealer-overview-listings"
              />
              <OverviewCard
                icon={<Clock size={14} />}
                label="Pending Review"
                value={pendingCount}
                onClick={() => { setListFilter("pending"); nav("/dealer/listings?filter=pending"); }}
                testid="dealer-overview-pending"
              />
              <OverviewCard
                icon={<MessageSquare size={14} />}
                label="My Leads"
                value={enquiries.length}
                onClick={() => nav("/dealer/leads")}
                testid="dealer-overview-leads"
              />
              <OverviewCard
                icon={<CheckCircle size={14} />}
                label="Sold"
                value={soldCount}
                onClick={() => { setListFilter("sold"); nav("/dealer/listings?filter=sold"); }}
                testid="dealer-overview-sold"
              />
            </div>
          );
        })()}

        <Tabs value={tab} onValueChange={onTabChange} data-testid="dealer-tabs">
          {!embedded && (
          <TabsList className="bg-white border border-[#E4E4E7] rounded-sm flex-wrap h-auto">
            <TabsTrigger value="submit" className="rounded-sm" data-testid="dealer-tab-submit"><Plus size={14} className="mr-1.5" /> Submit</TabsTrigger>
            <TabsTrigger value="submissions" className="rounded-sm" data-testid="dealer-tab-submissions"><Layers size={14} className="mr-1.5" /> My Listings ({submissions.length})</TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-sm" data-testid="dealer-tab-enquiries"><MessageSquare size={14} className="mr-1.5" /> Leads ({enquiries.length})</TabsTrigger>
            {/* v2.35 — PART 2: Earnings tab hidden for builders (own inventory, no commission) */}
            {!isBuilder && (
              <TabsTrigger value="commission" className="rounded-sm" data-testid="dealer-tab-commission"><Wallet size={14} className="mr-1.5" /> Earnings</TabsTrigger>
            )}
            <TabsTrigger value="kyc" className="rounded-sm" data-testid="dealer-tab-kyc"><ShieldCheck size={14} className="mr-1.5" /> KYC</TabsTrigger>
            <TabsTrigger value="projects" className="rounded-sm" data-testid="dealer-tab-projects"><Building2 size={14} className="mr-1.5" /> Projects</TabsTrigger>
            <TabsTrigger value="explore" className="rounded-sm" data-testid="dealer-tab-explore"><Layers size={14} className="mr-1.5" /> Marketplace</TabsTrigger>
            {!freeMode && (
              <TabsTrigger value="plan" className="rounded-sm" data-testid="dealer-tab-plan"><Crown size={14} className="mr-1.5" /> My Plan {myPlan.active && <span className="ml-1.5 bg-[#065F46] text-white text-[9px] px-1 rounded-sm">ACTIVE</span>}</TabsTrigger>
            )}
          </TabsList>
          )}

          {/* Submit form (Unified, filter-driven) */}
          <TabsContent value="submit" className="mt-5">
            {!freeMode && listingStatus?.must_upgrade ? (
              <div className="bg-[#FEE2E2] border border-[#FECACA] text-[#991B1B] p-5 rounded-sm" data-testid="dealer-quota-block">
                <div className="font-heading font-semibold text-lg flex items-center gap-2"><AlertTriangle size={16} /> Free quota reached</div>
                <p className="text-[13px] mt-1">You've used all {listingStatus.free_quota} free listings. Contact admin to enable a subscription.</p>
              </div>
            ) : (
              <Suspense fallback={<DashboardSkeleton stats={0} rows={5} />}>
                <UnifiedSubmissionForm
                  role="dealer"
                  onSaved={() => setTab("submissions")}
                />
              </Suspense>
            )}
          </TabsContent>

          {/* My submissions */}
          <TabsContent value="submissions" className="mt-5">
            <div className="space-y-3">
              {/* v2.71.7 — Filter chip row (driven by overview-card clicks) */}
              <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="dealer-submissions-filter-row" data-results-anchor="dealer-submissions">
                {[
                  { k: "all", label: `All (${submissions.length})` },
                  { k: "pending", label: `Pending (${submissions.filter(s => s.approval_status === "pending").length})` },
                  { k: "approved", label: `Approved (${submissions.filter(s => s.approval_status === "approved").length})` },
                  { k: "sold", label: `Sold (${submissions.filter(s => s.status === "Sold").length})` },
                ].map(({ k, label }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => {
                      setListFilter(k);
                      const qp = k === "all" ? "" : `?filter=${k}`;
                      if (embedded) nav(`/dealer/listings${qp}`, { replace: true });
                      else nav(`/dealer?tab=submissions${k === "all" ? "" : `&filter=${k}`}`, { replace: true });
                      scrollToResults({ anchor: "dealer-submissions" });
                    }}
                    className={`text-[11.5px] font-semibold rounded-full px-3 py-1 border transition-all ${
                      listFilter === k
                        ? "bg-[#0055FF] text-white border-[#0055FF]"
                        : "bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#0055FF]/40"
                    }`}
                    data-testid={`dealer-submissions-filter-${k}`}
                  >
                    {label}
                  </button>
                ))}
                {/* v2.71.53 — Card/Table view toggle, default cards. Pushes to the right. */}
                <div className="ml-auto">
                  <ViewToggle value={submissionsView} onChange={setSubmissionsView} testidPrefix="dealer-submissions-view" />
                </div>
              </div>
              {(() => {
                const visible = submissions.filter((s) => {
                  if (listFilter === "pending") return s.approval_status === "pending";
                  if (listFilter === "approved") return s.approval_status === "approved";
                  if (listFilter === "sold") return s.status === "Sold";
                  return true;
                });
                if (submissions.length === 0) {
                  return <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm">No listings yet.</div>;
                }
                if (visible.length === 0) {
                  return <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm">No listings match this filter.</div>;
                }
                // v2.71.53 — Card view (default) renders horizontal cards; table view kept for column-scanning power users.
                if (submissionsView === "cards") {
                  return (
                    <div className="space-y-2" data-testid="dealer-submissions-cards">
                      {visible.map((s) => (
                        <PropertyCard
                          key={s.id}
                          property={s}
                          variant={isBuilder ? "builder" : "dealer"}
                          onEdit={(user?.permissions?.edit_listing !== false)
                            ? (prop) => { setEditingListing(prop); setShowListingForm(true); }
                            : null}
                          onDelete={(user?.permissions?.delete_listing !== false)
                            ? async (id) => {
                                if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
                                try {
                                  await api.delete(`/properties/${id}`);
                                  toast.success("Deleted");
                                  emitDataChange(["properties", "approvals", "dashboard"], { action: "delete", id });
                                } catch (err) {
                                  toast.error(apiErrorMsg(err, "Delete failed"));
                                }
                              }
                            : null}
                          onResubmit={async (id) => {
                            try {
                              await api.post(`/dealer/properties/${id}/submit`);
                              toast.success("Submitted for review");
                              emitDataChange(["properties", "approvals", "dashboard"]);
                            } catch (err) {
                              toast.error(apiErrorMsg(err, "Submit failed"));
                            }
                          }}
                          testidPrefix="dealer-submission"
                        />
                      ))}
                    </div>
                  );
                }
                // Legacy table-row layout (preserved for power users)
                return visible.map((s) => {
                const v = STATUS_VISUAL[s.approval_status] || STATUS_VISUAL.pending;
                const isSold = s.status === "Sold";
                const lockedFields = s.locked_fields || [];
                return (
                  <div key={s.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`dealer-submission-${s.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-heading font-semibold text-[15px] tracking-tight">{s.title}</h4>
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm ${v.cls}`} data-testid={`status-badge-${s.id}`}>{v.icon} {v.label}</span>
                          {isSold && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm bg-[#09090B] text-white border-[#09090B]"><CheckCircle size={10} /> Sold</span>}
                          {s.assigned_by_admin && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]" data-testid={`assigned-badge-${s.id}`}>
                              <ShieldCheck size={10} /> Assigned by Admin
                            </span>
                          )}
                          {lockedFields.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]" title={`Locked: ${lockedFields.join(", ")}`} data-testid={`locked-badge-${s.id}`}>
                              <Lock size={10} /> {lockedFields.length} locked
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-[#52525B] mt-1">
                          {s.main_area || s.area} {s.sub_area && `· ${s.sub_area}`}{s.bhk && s.bhk !== "Not Specified" ? ` · ${s.bhk}` : ""} · {s.property_type} · <span className="text-[#0055FF] font-semibold">{formatINR(s.price)}</span>{s.size_sqft ? ` · ${formatSize(s.size_sqft, s.size_unit)}` : ""}
                        </div>
                        <div className="text-[11px] text-[#A1A1AA] mt-1">Submitted {new Date(s.created_at).toLocaleString()}</div>
                      </div>
                      {(s.approval_status === "draft" || s.approval_status === "rejected") && (
                        <Button
                          size="sm"
                          className="btn-brand rounded-sm flex-shrink-0"
                          onClick={async () => {
                            try {
                              await api.post(`/dealer/properties/${s.id}/submit`);
                              toast.success("Submitted for review");
                              emitDataChange(["properties", "approvals", "dashboard"]);
                            } catch (err) {
                              toast.error(apiErrorMsg(err, "Submit failed"));
                            }
                          }}
                          data-testid={`dealer-resubmit-${s.id}`}
                        >
                          <Send size={11} className="mr-1" /> {s.approval_status === "rejected" ? "Resubmit" : "Submit"}
                        </Button>
                      )}
                      {/* v2.54 — Edit / Delete own listing buttons (gated by per-user permissions) */}
                      {(user?.permissions?.edit_listing !== false) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm flex-shrink-0"
                          onClick={() => { setEditingListing(s); setShowListingForm(true); }}
                          data-testid={`dealer-edit-listing-${s.id}`}
                        >
                          <FileEdit size={11} className="mr-1" /> Edit
                        </Button>
                      )}
                      {(user?.permissions?.delete_listing !== false) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-sm text-[#DC2626] hover:bg-[#FEE2E2] flex-shrink-0"
                          onClick={async () => {
                            if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
                            try {
                              await api.delete(`/properties/${s.id}`);
                              toast.success("Deleted");
                              emitDataChange(["properties", "approvals", "dashboard"], { action: "delete", id: s.id });
                            } catch (err) {
                              toast.error(apiErrorMsg(err, "Delete failed"));
                            }
                          }}
                          data-testid={`dealer-delete-listing-${s.id}`}
                        >
                          <X size={11} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                    {s.approval_status === "rejected" && s.rejection_reason && (
                      <div className="mt-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm p-2.5" data-testid={`rejection-reason-${s.id}`}>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#991B1B] inline-flex items-center gap-1 mb-1">
                          <AlertTriangle size={10} /> Admin feedback
                        </div>
                        <div className="text-[12px] text-[#7F1D1D]">{s.rejection_reason}</div>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          </TabsContent>

          {/* Masked enquiries */}
          <TabsContent value="enquiries" className="mt-5">
            <div className="text-[11px] text-[#A1A1AA] mb-3 flex items-center gap-1.5">
              <Phone size={11} /> Client contact is masked — admin handles direct communication. You see lead counts and property references.
            </div>
            <div className="space-y-2">
              {enquiries.length === 0 && (
                <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm" data-testid="dealer-enquiries-empty">
                  No leads on your properties yet.
                </div>
              )}
              {enquiries.map((e) => (
                <div key={e.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`dealer-enq-${e.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-heading font-semibold text-[15px] tracking-tight">{e.name}</h4>
                    <span className="text-[11px] text-[#52525B] font-mono-data">{e.phone}</span>
                  </div>
                  <div className="text-[12px] text-[#52525B] mt-1">
                    {e.property_title} · {e.property_area}
                  </div>
                  <div className="text-[11px] text-[#A1A1AA] mt-1">{new Date(e.created_at).toLocaleString()} · status: {e.status}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Commission — hidden for builders (own inventory, no commission per PART 2) */}
          {!isBuilder && (
          <TabsContent value="commission" className="mt-5">
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <Metric label="Sold properties" value={commission?.sold_count ?? 0} />
              <Metric label="Total commission" value={formatINR(commission?.total_commission || 0)} accent />
              <Metric label={`Your share (${commission?.share_pct ?? 50}%)`} value={formatINR(commission?.dealer_share || 0)} />
            </div>
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="commission-summary">
              <h3 className="font-heading font-semibold text-lg tracking-tight">Payout terms</h3>
              <p className="text-[13px] text-[#52525B] mt-2">
                {commission?.payout_note || "After registry + 7 days"}. Commission is set by admin per property{commission?.share_pct_source === "custom" ? " — your custom share is configured by admin" : ""}. Questions about a specific deal? Email <a href="mailto:houseentrysolutions@gmail.com" className="text-[#0055FF] hover:underline">houseentrysolutions@gmail.com</a>.
              </p>
            </div>
          </TabsContent>
          )}

          {/* KYC */}
          <TabsContent value="kyc" className="mt-5">
            <KYCSection kyc={kyc} verified={verified} onDone={async () => { await load(); await refreshUser?.(); }} />
          </TabsContent>

          {/* Projects — Builder + Dealer + Admin can all create */}
          <TabsContent value="projects" className="mt-5">
            <BuilderProjectsTab readOnly={false} />
          </TabsContent>

          <TabsContent value="explore" className="mt-5">
            <DealerExplore hasActivePlan={freeMode || myPlan.active} onUpgradeClick={() => setTab("plan")} />
          </TabsContent>

          {!freeMode && (
            <TabsContent value="plan" className="mt-5">
              <DealerPlanCard onChange={load} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* v2.54 — Phase C Part 2: Edit own listing dialog (uses UnifiedSubmissionForm in dealer mode) */}
      <Dialog open={showListingForm} onOpenChange={(v) => { if (!v) { setShowListingForm(false); setEditingListing(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="dealer-edit-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">Edit Listing</DialogTitle>
          </DialogHeader>
          {editingListing && (
            <Suspense fallback={<DashboardSkeleton stats={0} rows={5} />}>
              <UnifiedSubmissionForm
                role="dealer"
                initial={editingListing}
                lockedFields={editingListing?.locked_fields || []}
                onSaved={() => { setShowListingForm(false); setEditingListing(null); load(); }}
              />
            </Suspense>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, optional }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1 flex items-center gap-1.5">
      {label}
      {optional && <span className="text-[9px] font-normal text-[#A1A1AA] normal-case tracking-normal">(optional)</span>}
    </div>
    {children}
  </div>
);

const Metric = ({ label, value, accent }) => (
  <div className={`p-4 rounded-sm border ${accent ? "bg-[#EEF4FF] border-[#0055FF]" : "bg-white border-[#E4E4E7]"}`} data-testid={`metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</div>
    <div className={`font-heading font-bold text-2xl font-mono-data mt-1 ${accent ? "text-[#0055FF]" : "text-[#09090B]"}`}>{value}</div>
  </div>
);

function KYCSection({ kyc, verified, onDone }) {
  const [form, setForm] = useState({ full_name: "", id_type: "Aadhaar", id_image_url: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, id_image_url: r.data.url }));
      toast.success("ID uploaded");
    } catch (err) { toast.error(apiErrorMsg(err, "Upload failed")); }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_image_url) {
      toast.error("Upload your ID first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/dealer/kyc", form);
      toast.success("KYC submitted — admin will review");
      onDone?.();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    setBusy(false);
  };

  const status = kyc?.status;

  return (
    <div className="max-w-xl">
      {verified ? (
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm p-5" data-testid="kyc-verified">
          <div className="flex items-center gap-2 text-[#15803D] font-semibold"><BadgeCheck size={18} /> Verified Partner</div>
          <p className="text-[13px] text-[#52525B] mt-2">
            Your account is KYC-verified. A "Verified Partner" badge now shows on your listings to build client trust.
          </p>
        </div>
      ) : status === "pending" ? (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-5" data-testid="kyc-pending">
          <div className="flex items-center gap-2 text-[#92400E] font-semibold"><Clock size={18} /> KYC under review</div>
          <p className="text-[13px] text-[#52525B] mt-2">Admin will verify your document shortly.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-[#E4E4E7] rounded-sm p-5 space-y-4" data-testid="kyc-form">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#0055FF]" />
              <h3 className="font-heading font-semibold text-xl tracking-tight">Complete KYC → get Verified Badge</h3>
            </div>
            <p className="text-[12px] text-[#52525B] mt-1">Clients trust verified partners more. Upload a government ID — admin will verify within 24h.</p>
            {status === "rejected" && (
              <div className="mt-2 p-2 text-[12px] bg-[#FEE2E2] border border-[#FECACA] text-[#991B1B] rounded-sm">
                Previous submission was rejected. Try again with a clearer document.
              </div>
            )}
          </div>
          <F label="Full name on ID">
            <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-sm" data-testid="kyc-name" />
          </F>
          <F label="ID type">
            <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="kyc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ID_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-2">ID image</div>
            {form.id_image_url ? (
              <div className="w-40 h-28 border border-[#E4E4E7] rounded-sm overflow-hidden relative">
                <img loading="lazy" src={resolveImage(form.id_image_url)} alt="id" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setForm({ ...form, id_image_url: "" })} className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-sm"><X size={12} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} className="border border-dashed border-[#E4E4E7] text-[#52525B] hover:border-[#0055FF] hover:text-[#0055FF] w-40 h-28 flex items-center justify-center rounded-sm" data-testid="kyc-upload-btn">
                <Upload size={20} />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          </div>
          <Button type="submit" disabled={busy} className="btn-brand rounded-sm" data-testid="kyc-submit">
            {busy ? "Submitting…" : "Submit KYC"}
          </Button>
        </form>
      )}
    </div>
  );
}

// v2.71.7 — Clickable overview card shared across Dealer + Builder dashboards.
// Mirrors the Admin StatCard pattern but takes a direct onClick (since dealer
// uses internal Tab state, not just react-router navigation).
function OverviewCard({ icon, label, value, onClick, accent, testid }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      className={`group bg-white border ${accent ? "border-[#0055FF]" : "border-[#E4E4E7]"} p-4 sm:p-5 rounded-sm cursor-pointer hover:border-[#0055FF] hover:shadow-md hover:-translate-y-0.5 transition-all relative`}
      data-testid={testid}
      aria-label={`Open ${label}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={accent ? "text-[#0055FF]" : "text-[#52525B]"}>{icon}</span>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</div>
        <ArrowUpRight size={12} className="ml-auto text-[#A1A1AA] group-hover:text-[#0055FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      </div>
      <div className={`font-heading font-bold text-2xl sm:text-3xl font-mono-data ${accent ? "text-[#0055FF]" : "text-[#09090B]"}`}>
        {value}
      </div>
    </div>
  );
}

